import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { todayISO, formatEuro } from "../lib/format";

/**
 * Quick-add payment dialog.
 * Props:
 *   open, onOpenChange — controlled open state
 *   debt — { id, code, owed_to, monthly_installment, debt_left }
 *   onSaved — callback after successful save
 */
export default function PaymentDialog({ open, onOpenChange, debt, onSaved }) {
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && debt) {
      setAmount(debt.monthly_installment ? String(debt.monthly_installment) : "");
      setDate(todayISO());
      setNotes("");
    }
  }, [open, debt]);

  if (!debt) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/debts/${debt.id}/payments`, {
        amount: Number(amount),
        payment_date: date,
        notes,
      });
      toast.success(`Payment recorded for ${debt.code}`);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add payment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white border-[#E5E3DB] rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1D3328]">Record payment</DialogTitle>
          <DialogDescription className="text-[#5C635A]">
            <span className="font-mono text-xs text-[#8B9389]">{debt.code}</span>{" "}
            <span className="text-[#1D3328] font-medium">{debt.owed_to}</span>
            {debt.debt_left != null && (
              <> · <span className="num">{formatEuro(debt.debt_left)}</span> remaining</>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" data-testid="payment-dialog-form">
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Amount (€)</Label>
            <Input
              type="number" step="0.01" min="0.01" required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="rounded-xl border-[#E5E3DB] num h-11"
              data-testid="payment-dialog-amount"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Date</Label>
            <Input
              type="date" required value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-xl border-[#E5E3DB] h-11"
              data-testid="payment-dialog-date"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Notes</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Installment 17/19"
              className="rounded-xl border-[#E5E3DB]"
              data-testid="payment-dialog-notes"
            />
          </div>

          <DialogFooter className="flex gap-2">
            <Button type="button" variant="outline"
                    onClick={() => onOpenChange(false)}
                    className="rounded-xl border-[#E5E3DB]"
                    data-testid="payment-dialog-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving}
                    className="rounded-xl bg-[#2C4A3B] hover:bg-[#3A5F4C] text-white"
                    data-testid="payment-dialog-submit">
              {saving ? "Recording…" : "Record payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
